import { useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  PieChart,
  Trophy,
  ArrowRight,
  Check,
  X,
  Sparkles,
  Shield,
  Clock,
  BarChart3,
  Users,
  Zap,
  ChevronRight,
  Mail,
  Quote,
  AlertCircle,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

// Reusable Email Form Component
function WaitlistForm({ variant = "hero" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("submitting");
    setErrorMsg("");
    
    try {
      // Save to Supabase waitlist table
      const { data, error } = await supabase
        .from("waitlist")
        .insert([
          { 
            email: email.trim().toLowerCase(),
            source: variant === "hero" ? "hero_form" : "bottom_cta",
          }
        ])
        .select();

      if (error) {
        // Check for duplicate email
        if (error.code === "23505" || error.message?.includes("duplicate")) {
          setErrorMsg("You're already on the list! We'll be in touch soon.");
          setStatus("error");
        } else {
          console.error("Waitlist error:", error);
          setErrorMsg("Something went wrong. Please try again.");
          setStatus("error");
        }
      } else {
        setStatus("success");
      }
    } catch (err) {
      console.error("Waitlist error:", err);
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-6 py-4">
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <p className="text-white font-semibold">You're on the list!</p>
          <p className="text-slate-400 text-sm">Keep an eye on your inbox for early access.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className={`flex flex-col sm:flex-row gap-3 ${variant === "hero" ? "max-w-xl" : ""}`}>
        <div className="relative flex-1">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            className="w-full bg-slate-900/80 border border-slate-700 focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 rounded-xl pl-12 pr-4 py-4 text-white placeholder-slate-500 text-sm outline-none transition-all duration-200"
          />
        </div>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-70 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm rounded-xl px-6 py-4 transition-all duration-200 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 whitespace-nowrap"
        >
          {status === "submitting" ? (
            <>
              <div className="w-4 h-4 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" />
              Joining...
            </>
          ) : (
            <>
              {variant === "hero" ? "Join Waitlist - Claim 50% Off" : "Lock In My 50% Discount"}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
      {status === "error" && (
        <div className="flex items-center gap-2 mt-3 text-amber-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{errorMsg}</p>
        </div>
      )}
      {status !== "error" && (
        <p className="text-slate-500 text-xs mt-3 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Join 1,000+ others securing early access. No spam, just wealth.
        </p>
      )}
    </form>
  );
}

// Feature Card Component
function FeatureCard({ icon: Icon, title, description, color = "emerald" }) {
  const colorClasses = {
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    sky: "bg-sky-500/10 text-sky-400 border-sky-500/20",
    violet: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-all duration-300 group">
      <div className={`w-12 h-12 rounded-xl ${colorClasses[color]} border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-outfit font-semibold text-white text-lg mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

// Comparison Item
function ComparisonItem({ text, isOld }) {
  return (
    <li className="flex items-start gap-3">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isOld ? "bg-red-500/20" : "bg-emerald-500/20"}`}>
        {isOld ? (
          <X className="w-3 h-3 text-red-400" />
        ) : (
          <Check className="w-3 h-3 text-emerald-400" />
        )}
      </div>
      <span className={isOld ? "text-slate-500" : "text-slate-300"}>{text}</span>
    </li>
  );
}

export default function BudgetPro() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-slate-950" strokeWidth={2.5} />
              </div>
              <span className="font-outfit font-bold text-white">Kara Finance</span>
            </Link>
            <Link
              to="/tools"
              className="text-slate-400 hover:text-white text-sm transition-colors"
            >
              Free Tools →
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background effects */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(16,185,129,0.15), transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.015]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Beta Badge */}
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-8 animate-pulse">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">Beta Launching Soon</span>
          </div>

          {/* Headline */}
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6">
            Spreadsheets are dead.
            <br />
            <span className="text-emerald-400">Meet your Financial Command Center.</span>
          </h1>

          {/* Sub-headline */}
          <p className="text-slate-400 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
            Stop manually updating clunky templates. Kara Finance Budget Pro is the interactive, 
            automated dashboard designed to plug the leaks in your bank account and accelerate 
            your path to financial freedom.
          </p>

          {/* Waitlist Form */}
          <div className="flex justify-center">
            <WaitlistForm variant="hero" />
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl border border-slate-800 bg-slate-900/50 p-2 shadow-2xl shadow-emerald-500/5">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
              </div>
              <div className="flex-1 bg-slate-800 rounded-lg px-4 py-1.5 text-center">
                <span className="text-slate-500 text-xs">app.karafinance.uk/dashboard</span>
              </div>
            </div>
            {/* Dashboard mockup */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <p className="text-slate-500 text-xs mb-1">Monthly Income</p>
                  <p className="text-white font-bold text-xl">£4,500</p>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <p className="text-slate-500 text-xs mb-1">Total Expenses</p>
                  <p className="text-red-400 font-bold text-xl">£2,840</p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <p className="text-slate-500 text-xs mb-1">Savings Rate</p>
                  <p className="text-emerald-400 font-bold text-xl">36.8%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-xl p-4 h-32 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full border-8 border-emerald-500/30 border-t-emerald-500 animate-spin-slow" style={{ animationDuration: '3s' }} />
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Needs (50%)</span>
                    <span className="text-white">£1,420</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-sky-500 h-2 rounded-full" style={{ width: "50%" }} />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Wants (30%)</span>
                    <span className="text-white">£852</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-violet-500 h-2 rounded-full" style={{ width: "30%" }} />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Savings (20%)</span>
                    <span className="text-emerald-400">£568</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{ width: "20%" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10x Value Comparison Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-4">
              Why it's <span className="text-emerald-400">10x more powerful</span> than traditional templates
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Your Excel sheet doesn't stand a chance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Old Way */}
            <div className="bg-slate-900/50 border border-red-500/20 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <X className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-red-400 text-xs font-medium uppercase tracking-wider">The Old Way</p>
                  <h3 className="font-outfit font-semibold text-white text-lg">Traditional Templates</h3>
                </div>
              </div>
              <ul className="space-y-4 text-sm">
                <ComparisonItem text="Manual data entry every single month" isOld />
                <ComparisonItem text="Static numbers that tell you nothing" isOld />
                <ComparisonItem text="Breaks if you accidentally delete a cell" isOld />
                <ComparisonItem text="Zero automated insights or warnings" isOld />
                <ComparisonItem text="No idea how you compare to others" isOld />
                <ComparisonItem text="Stuck on one device with local files" isOld />
              </ul>
            </div>

            {/* New Way */}
            <div className="bg-slate-900/50 border border-emerald-500/30 rounded-2xl p-8 relative overflow-hidden">
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse 100% 100% at 100% 0%, rgba(16,185,129,0.08), transparent 60%)",
                }}
              />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-400 text-xs font-medium uppercase tracking-wider">The Pro Way</p>
                    <h3 className="font-outfit font-semibold text-white text-lg">Kara Finance Pro</h3>
                  </div>
                </div>
                <ul className="space-y-4 text-sm">
                  <ComparisonItem text="Interactive visual dashboards that update in real-time" />
                  <ComparisonItem text="Smart categorization of Vital vs Non-Vital spending" />
                  <ComparisonItem text="'You vs UK Average' benchmarking on rent, groceries & bills" />
                  <ComparisonItem text="Automated spending audits that find the leaks" />
                  <ComparisonItem text="10-year net worth projections based on your habits" />
                  <ComparisonItem text="Secure cloud saving — access anywhere, any device" />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything you need to <span className="text-emerald-400">escape the rat race</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Powerful features designed specifically for UK investors building wealth.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={PieChart}
              title="The 50/30/20 Visualiser"
              description="Instantly see if you're suffering from lifestyle creep with our dynamic spending charts. Watch your budget balance in real-time."
              color="emerald"
            />
            <FeatureCard
              icon={Trophy}
              title="UK Benchmarking"
              description="Compare your rent, groceries, and bills against the UK average to find out exactly where you're overspending — and by how much."
              color="sky"
            />
            <FeatureCard
              icon={TrendingUp}
              title="The Financial Time Machine"
              description="Project your net worth 10 years into the future based on your current saving habits. See what small changes today mean for tomorrow."
              color="violet"
            />
            <FeatureCard
              icon={Shield}
              title="Vital vs Non-Vital Audit"
              description="Automatically categorize every expense as essential or optional. Finally see the true cost of convenience."
              color="emerald"
            />
            <FeatureCard
              icon={BarChart3}
              title="Export to Excel"
              description="Need your data in a spreadsheet? Export professional multi-tab reports with a single click. Best of both worlds."
              color="sky"
            />
            <FeatureCard
              icon={Clock}
              title="Subscription Tracker"
              description="Surface all your recurring charges in one place. Find and cancel the forgotten subscriptions draining your account."
              color="violet"
            />
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 sm:p-12 relative overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "radial-gradient(ellipse 60% 60% at 0% 100%, rgba(16,185,129,0.08), transparent 60%)",
              }}
            />
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              {/* Founder Image Placeholder */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-sky-500/20 border border-emerald-500/30 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-xl bg-slate-800 flex items-center justify-center">
                    <span className="text-4xl">👨‍💻</span>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <div className="text-center md:text-left">
                <Quote className="w-8 h-8 text-emerald-500/40 mb-4 mx-auto md:mx-0" />
                <blockquote className="text-white text-lg sm:text-xl leading-relaxed mb-4 font-medium">
                  "I built the tool I wished I had when I was starting with £0. This isn't just a 
                  calculator; it's a roadmap out of the rat race."
                </blockquote>
                <div>
                  <p className="text-emerald-400 font-semibold">Krish Kara</p>
                  <p className="text-slate-500 text-sm">Founder, Kara Finance • UK Investor</p>
                </div>
              </div>
            </div>

            {/* Built by badge */}
            <div className="mt-8 pt-8 border-t border-slate-800 flex items-center justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>Built by a UK investor</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Users className="w-4 h-4 text-emerald-500" />
                <span>For UK investors</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Zap className="w-4 h-4 text-emerald-500" />
                <span>No financial jargon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-b from-emerald-500/10 to-slate-900/50 border border-emerald-500/30 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
            {/* Glow effect */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "radial-gradient(ellipse 80% 80% at 50% 0%, rgba(16,185,129,0.2), transparent 60%)",
              }}
            />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 rounded-full px-4 py-1.5 mb-6">
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">Limited Time Offer</span>
              </div>

              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-4">
                Don't pay full price on launch day.
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                Join the waitlist now and lock in <span className="text-emerald-400 font-semibold">50% off</span> your 
                first year of Kara Finance Budget Pro. Only available to early supporters.
              </p>

              <div className="max-w-xl mx-auto">
                <WaitlistForm variant="cta" />
              </div>

              {/* Trust badges */}
              <div className="mt-8 pt-6 border-t border-emerald-500/20 flex items-center justify-center gap-6 flex-wrap text-slate-500 text-xs">
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  Cancel anytime
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  No credit card required
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  GDPR compliant
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-emerald-500 flex items-center justify-center">
              <TrendingUp className="w-3 h-3 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="font-outfit font-semibold text-white text-sm">Kara Finance</span>
          </div>
          <div className="flex items-center gap-6 text-slate-500 text-xs">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/tools" className="hover:text-white transition-colors">Free Tools</Link>
            <Link to="/strategies" className="hover:text-white transition-colors">Strategies</Link>
          </div>
          <p className="text-slate-600 text-xs">
            © 2026 Kara Finance UK. Not financial advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
